import { useEffect, useState } from "react";
import { Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { mergeContent } from "../../lib/siteContent";
import ImageUploadField from "./ImageUploadField";

// Dot-path get/set on a nested object (immutable set)
const getAt = (obj, path) => path.split(".").reduce((o, k) => (o == null ? o : o[k]), obj);
function setAt(obj, path, value) {
  const keys = path.split(".");
  const clone = Array.isArray(obj) ? [...obj] : { ...obj };
  let cur = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    cur[k] = Array.isArray(cur[k]) ? [...cur[k]] : { ...cur[k] };
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = value;
  return clone;
}

const SECTIONS = [
  {
    title: "Hero",
    fields: [
      { path: "hero.eyebrow", label: "Eyebrow line" },
      { path: "hero.heading_pre", label: "Heading — start" },
      { path: "hero.heading_accent1", label: "Heading — accent word 1" },
      { path: "hero.heading_mid", label: "Heading — middle" },
      { path: "hero.heading_accent2", label: "Heading — accent word 2" },
      { path: "hero.heading_post", label: "Heading — end" },
      { path: "hero.subtitle", label: "Subtitle", type: "textarea" },
      { path: "hero.cta_primary", label: "Primary button" },
      { path: "hero.cta_secondary", label: "Secondary button" },
      { path: "hero.image", label: "Hero background image", type: "image" },
    ],
  },
  {
    title: "Homepage Stats (leave a value blank to hide that stat)",
    isStats: true,
  },
  {
    title: "Services Section",
    fields: [
      { path: "services_section.eyebrow", label: "Eyebrow" },
      { path: "services_section.title_pre", label: "Title — start" },
      { path: "services_section.title_accent", label: "Title — accent" },
      { path: "services_section.cta", label: "CTA button" },
    ],
  },
  {
    title: "Work / Portfolio Section",
    fields: [
      { path: "portfolio_section.eyebrow", label: "Eyebrow" },
      { path: "portfolio_section.title_pre", label: "Title — start" },
      { path: "portfolio_section.title_accent", label: "Title — accent" },
    ],
  },
  {
    title: "Founder Section",
    fields: [
      { path: "founder.eyebrow", label: "Eyebrow" },
      { path: "founder.initials", label: "Avatar initials" },
      { path: "founder.name", label: "Name" },
      { path: "founder.role", label: "Role line" },
      { path: "founder.short", label: "Short caption" },
      { path: "founder.heading_pre", label: "Heading — start" },
      { path: "founder.heading_accent1", label: "Heading — accent 1" },
      { path: "founder.heading_mid", label: "Heading — middle" },
      { path: "founder.heading_accent2", label: "Heading — accent 2" },
      { path: "founder.heading_post", label: "Heading — end" },
      { path: "founder.bio", label: "Bio", type: "textarea" },
      { path: "founder.stat1_v", label: "Stat 1 value" },
      { path: "founder.stat1_l", label: "Stat 1 label" },
      { path: "founder.stat2_v", label: "Stat 2 value" },
      { path: "founder.stat2_l", label: "Stat 2 label" },
      { path: "founder.image", label: "Founder background image", type: "image" },
    ],
  },
  {
    title: "Closing CTA Section",
    fields: [
      { path: "cta_section.title_pre", label: "Title — start" },
      { path: "cta_section.title_accent", label: "Title — accent" },
      { path: "cta_section.title_post", label: "Title — end" },
      { path: "cta_section.subtitle", label: "Subtitle", type: "textarea" },
      { path: "cta_section.button", label: "Button" },
    ],
  },
  {
    title: "Services Page Header",
    fields: [
      { path: "services_page.eyebrow", label: "Eyebrow" },
      { path: "services_page.title_pre", label: "Title — middle (after count)" },
      { path: "services_page.title_accent", label: "Title — accent" },
      { path: "services_page.subtitle", label: "Subtitle", type: "textarea" },
    ],
  },
  {
    title: "Work Page Header",
    fields: [
      { path: "portfolio_page.eyebrow", label: "Eyebrow" },
      { path: "portfolio_page.title_pre", label: "Title — start" },
      { path: "portfolio_page.title_accent", label: "Title — accent" },
      { path: "portfolio_page.subtitle", label: "Subtitle", type: "textarea" },
    ],
  },
];

function Field({ label, value, onChange, type, testId }) {
  if (type === "image") {
    return <ImageUploadField label={label} value={value} onChange={onChange} testId={testId} />;
  }
  return (
    <div>
      <label className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72]">{label}</label>
      {type === "textarea" ? (
        <textarea
          value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} data-testid={testId}
          className="mt-2 w-full px-3.5 py-2.5 rounded-lg bg-white border border-black/[0.08] focus:border-[#00D4FF]/60 outline-none text-sm text-[#0E0E1A] leading-relaxed"
        />
      ) : (
        <input
          type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}
          className="mt-2 w-full px-3.5 py-2.5 rounded-lg bg-white border border-black/[0.08] focus:border-[#00D4FF]/60 outline-none text-sm text-[#0E0E1A]"
        />
      )}
    </div>
  );
}

export default function AdminContentEditor() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/content");
        setContent(mergeContent(data));
      } catch (e) {
        setContent(mergeContent({}));
      }
    })();
  }, []);

  const update = (path, value) => setContent((c) => setAt(c, path, value));
  const updateStat = (idx, key, value) =>
    setContent((c) => {
      const stats = [...(c.stats || [])];
      stats[idx] = { ...stats[idx], [key]: value };
      return { ...c, stats };
    });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", content);
      toast.success("Content saved — live on the site");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!content) {
    return <div className="glass-card rounded-2xl p-10 text-center text-[#5A5A72]">Loading content…</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between sticky top-0 z-10 bg-[#F8F8F6]/90 backdrop-blur py-2">
        <p className="text-sm text-[#5A5A72]">Edit any text or image, then save. Changes go live instantly.</p>
        <button onClick={save} disabled={saving} data-testid="content-save-btn" className="btn-primary px-5 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
        </button>
      </div>

      {SECTIONS.map((section) => (
        <div key={section.title} className="glass-card rounded-2xl p-6">
          <h3 className="font-display font-bold text-lg mb-5">{section.title}</h3>
          {section.isStats ? (
            <div className="grid sm:grid-cols-2 gap-4">
              {(content.stats || []).map((st, idx) => (
                <div key={idx} className="grid grid-cols-2 gap-3 p-3 rounded-xl border border-black/[0.06]">
                  <Field label={`Stat ${idx + 1} value`} value={st.value} onChange={(v) => updateStat(idx, "value", v)} testId={`stat-${idx}-value`} />
                  <Field label="Label" value={st.label} onChange={(v) => updateStat(idx, "label", v)} testId={`stat-${idx}-label`} />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-5">
              {section.fields.map((f) => (
                <div key={f.path} className={f.type === "textarea" || f.type === "image" ? "md:col-span-2" : ""}>
                  <Field
                    label={f.label}
                    type={f.type}
                    value={getAt(content, f.path)}
                    onChange={(v) => update(f.path, v)}
                    testId={`content-${f.path.replace(/\./g, "-")}`}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="btn-primary px-5 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
        </button>
      </div>
    </div>
  );
}
